import { useMemo, useState } from 'react';
import {
  MAX_PRICE,
  MIN_PRICE,
  PRICE_MESSAGE_KEY,
  isPriceOutOfBounds,
  priceMessageParams,
} from '~/utils/moneyBounds';
import { useTranslation } from 'react-i18next';
import { useAllPlatformProducts } from '~/api/platformProducts.tsx';
import { useGetPlatformProductsLengthsForProduct } from '~/api/inventory.tsx';
import { Button } from '~/components/ui';
import { Input } from '~/components/ui/inputs/Input';
import { SearchableSelect } from '~/components/ui/inputs/SearchableSelect';
import { ModalHeader } from '~/components/overlays';
import { BundleQuantityInput } from '~/components/ui/BundleQuantityInput';
import { formatLengthWithBasis } from '~/utils';
import type {
  PlatformProductsLengthsForProductQuery,
  Quantity,
  UnitCode,
} from '~/graphql/graphql.ts';

type PlatformProductsLength = NonNullable<
  PlatformProductsLengthsForProductQuery['platformProductsLengthsForProduct']
>[number];

type AddPackingListItemModalProps = {
  readonly currency: string;
  readonly usedPplIds: Set<string>;
  readonly onClose: () => void;
  readonly onAdd: (item: {
    platformProductLength: PlatformProductsLength;
    quantity: Quantity;
    price: number;
    piecesPerBundleOverride?: number;
  }) => void;
};

export function AddPackingListItemModal({
  currency,
  usedPplIds,
  onClose,
  onAdd,
}: AddPackingListItemModalProps) {
  const { t } = useTranslation('order');
  const { data, isLoading, error } = useAllPlatformProducts();
  const [selectedProductId, setSelectedProductId] = useState('');
  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedLength, setSelectedLength] =
    useState<PlatformProductsLength | null>(null);
  const [quantity, setQuantity] = useState<Quantity>({
    value: 0,
    unit: 'PCS' as UnitCode,
  });
  const [priceInput, setPriceInput] = useState('');
  const [piecesPerBundleOverride, setPiecesPerBundleOverride] =
    useState<number>();

  const products = useMemo(
    () => (Array.isArray(data?.platformProducts) ? data.platformProducts : []),
    [data]
  );

  const hasValidProductId = selectedProductId.trim().length > 0;

  const {
    data: lengthsData,
    isLoading: lengthsLoading,
    error: lengthsError,
  } = useGetPlatformProductsLengthsForProduct(selectedProductId, {
    enabled: hasValidProductId,
  });

  const availableLengths = useMemo(() => {
    const lengths = Array.isArray(
      lengthsData?.platformProductsLengthsForProduct
    )
      ? lengthsData.platformProductsLengthsForProduct
      : [];
    return lengths.filter((l) => l && !usedPplIds.has(l.id));
  }, [lengthsData, usedPplIds]);

  const price = parseFloat(priceInput);

  const priceError =
    priceInput === ''
      ? undefined
      : isPriceOutOfBounds(price)
        ? t(PRICE_MESSAGE_KEY, priceMessageParams)
        : isNaN(price)
          ? t('order.packing_list.price_invalid')
          : undefined;

  const canSubmit =
    hasValidProductId &&
    !!selectedLength &&
    quantity.value > 0 &&
    !isNaN(price) &&
    !isPriceOutOfBounds(price);

  const handleChooseProduct = (productId: string) => {
    if (!productId) {
      setSelectedProductId('');
      setSelectedProductName('');
      setSelectedLength(null);
      return;
    }
    const product = products.find((p) => p.id === productId);
    if (product) {
      setSelectedProductId(product.id);
      setSelectedProductName(product.name ?? '');
      setSelectedLength(null);
    }
  };

  const handleChooseLength = (lengthId: string) => {
    const length = availableLengths.find((l) => l?.id === lengthId) ?? null;
    setSelectedLength(length ?? null);
    setPiecesPerBundleOverride(undefined);
    if (length) {
      setQuantity({ value: 0, unit: length.platformProduct.standardUnitsCode });
    }
  };

  const handleAdd = () => {
    if (!canSubmit || !selectedLength) return;
    onAdd({
      platformProductLength: selectedLength,
      quantity,
      price,
      piecesPerBundleOverride,
    });
  };

  const lengthSelectPlaceholder = (() => {
    if (!hasValidProductId) return t('order.packing_list.select_product_first');
    if (lengthsLoading) return t('order.packing_list.lengths_loading');
    if (lengthsError) return t('order.packing_list.lengths_failed');
    if (availableLengths.length === 0)
      return t('order.packing_list.lengths_empty');
    return t('order.packing_list.select_length');
  })();

  return (
    <div className="bg-neut-0 rounded-lg shadow-xl">
      <ModalHeader
        title={t('order.packing_list.add_item_title')}
        onClose={onClose}
      />

      <div className="p-6 space-y-4">
        <SearchableSelect
          label={t('order.packing_list.product_label')}
          name="add-item-product"
          value={selectedProductId}
          onChange={(_name, value) => handleChooseProduct(value)}
          options={products.map((p) => ({ value: p.id, label: p.name ?? '' }))}
          placeholder={t('order.packing_list.select_product_placeholder')}
          disabled={isLoading || !!error}
          required
        />

        <div>
          <label
            htmlFor="add-item-length-select"
            className="block text-sm font-medium text-neut-700 mb-2"
          >
            {t('order.packing_list.length_label')}{' '}
            <span className="text-error-500">*</span>
          </label>
          <select
            id="add-item-length-select"
            className="w-full px-4 py-2 border border-neut-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-prim bg-neut-0"
            disabled={
              !hasValidProductId ||
              lengthsLoading ||
              !!lengthsError ||
              availableLengths.length === 0
            }
            value={selectedLength?.id ?? ''}
            onChange={(e) => handleChooseLength(e.target.value)}
          >
            <option value="">{lengthSelectPlaceholder}</option>
            {availableLengths.map(
              (l) =>
                l && (
                  <option key={l.id} value={l.id}>
                    {formatLengthWithBasis(l.lengthDisplayValue, l.isNominal)}
                  </option>
                )
            )}
          </select>
        </div>

        <div>
          <span className="block text-sm font-medium text-neut-700 mb-1">
            {t('order.packing_list.qty_column')}{' '}
            <span className="text-error-500">*</span>
          </span>
          <div className="min-h-32 flex flex-col">
            {selectedLength ? (
              <BundleQuantityInput
                quantity={quantity}
                platformProductsLength={selectedLength}
                piecesPerBundle={piecesPerBundleOverride}
                onPiecesPerBundleChange={(
                  value,
                  quantityKeepingBundleCount
                ) => {
                  setPiecesPerBundleOverride(value);
                  setQuantity(quantityKeepingBundleCount);
                }}
                onPiecesPerBundleClear={(quantityKeepingBundleCount) => {
                  setPiecesPerBundleOverride(undefined);
                  setQuantity(quantityKeepingBundleCount);
                }}
                onBundleChange={setQuantity}
              />
            ) : (
              <p className="text-sm text-neut-400 border border-dashed border-neut-300 rounded-lg px-3 py-4 text-center">
                {t('order.packing_list.select_length_first')}
              </p>
            )}
          </div>
        </div>

        <Input
          label={t('order.packing_list.price_label', { currency })}
          required
          name="add-item-price"
          type="number"
          min={MIN_PRICE}
          max={MAX_PRICE}
          step="any"
          value={priceInput}
          onChange={(_name, value) => setPriceInput(value)}
          error={priceError}
        />

        <p className="text-xs text-neut-500">{selectedProductName}</p>
      </div>

      <div className="px-6 py-4 border-t border-neut-200 flex items-center justify-end gap-3">
        <Button variant="secondary" onClick={onClose}>
          {t('order.packing_list.cancel_button')}
        </Button>
        <Button disabled={!canSubmit} onClick={handleAdd}>
          {t('order.packing_list.add_item_submit')}
        </Button>
      </div>
    </div>
  );
}
